// src/services/authService.js
import api from '../utils/api';
import {
  clearStoredAuth,
  getStoredTokenSync,
  getStoredUserSync,
  setStoredAuth
} from '../utils/authStorage';

const authService = {
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      if (response.data.token) {
        await setStoredAuth({
          token: response.data.token,
          user: response.data.user
        });
      }
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { message: 'Network error' };
    }
  },

  login: async (credentials) => {
    try {
      console.log('🔐 Attempting login with API URL:', api.defaults.baseURL);
      const response = await api.post('/auth/login', credentials);
      
      if (response.data.token) {
        if (response.data.user && response.data.user._id && !response.data.user.id) {
          response.data.user.id = response.data.user._id;
        }
        
        if (!response.data.user.branchPermissions) {
          response.data.user.branchPermissions = { menu: [], tables: [] };
        } else {
          if (!response.data.user.branchPermissions.menu) {
            response.data.user.branchPermissions.menu = [];
          }
          if (!response.data.user.branchPermissions.tables) {
            response.data.user.branchPermissions.tables = [];
          }
        }
        
        await setStoredAuth({
          token: response.data.token,
          user: response.data.user
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Login error:', {
        hasResponse: !!error.response,
        status: error.response?.status,
        message: error.message,
        code: error.code,
        baseURL: api.defaults.baseURL
      });
      
      // Provide more specific error messages
      if (!error.response) {
        // Network error - server not reachable
        if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
          const currentUrl = api.defaults.baseURL;
          const isLocalhost = currentUrl.includes('localhost') || currentUrl.includes('127.0.0.1');
          
          let errorMessage = `Cannot connect to server at ${currentUrl}\n\nTroubleshooting steps:\n1. Ensure server is running on port 5000\n2. Ensure device is on the same Wi-Fi network\n3. Check firewall allows port 5000`;
          
          if (isLocalhost) {
            errorMessage += `\n\n⚠️ Detected localhost URL. If accessing from mobile:\n- Get your computer's IP address (e.g., 192.168.1.100)\n- Open browser console and run:\n  localStorage.setItem('api_url', 'http://YOUR_IP:5000/api')\n- Or add ?api_url=http://YOUR_IP:5000/api to the URL\n- Then refresh the page`;
          }
          
          throw new Error(errorMessage);
        } else if (error.code === 'ECONNABORTED') {
          throw new Error('Connection timeout. Server may be slow or unreachable.');
        } else {
          throw new Error(`Network error: ${error.message || 'Unable to connect to server'}`);
        }
      }
      
      // Server responded with error
      throw error.response?.data || { message: 'Login failed. Please check your credentials.' };
    }
  },

  // Verify the 6-digit signup code; on success the backend returns a login token.
  verifyEmailCode: async ({ email, code }) => {
    try {
      const response = await api.post('/auth/verify-code', { email, code });
      if (response.data.token) {
        await setStoredAuth({
          token: response.data.token,
          user: response.data.user
        });
      }
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { message: 'Network error' };
    }
  },

  resendCode: async (email) => {
    try {
      const response = await api.post('/auth/resend-code', { email });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { message: 'Network error' };
    }
  },

  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { message: 'Network error' };
    }
  },

  resetPassword: async (token, password) => {
    try {
      const response = await api.post('/auth/reset-password', { token, password });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { message: 'Network error' };
    }
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/user');
      return response.data;
    } catch (error) {
      // Only clear auth when the server explicitly rejected the token.
      // Network errors / 5xx (server asleep, no internet, timeouts) must NOT
      // log the user out — the token is still valid.
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        await clearStoredAuth();
      }
      throw error.response ? error.response.data : { message: 'Network error' };
    }
  },

  logout: async () => {
    await clearStoredAuth();
  },

  isAuthenticated: () => {
    const token = getStoredTokenSync();
    return token !== null;
  },

  getUserFromStorage: () => {
    return getStoredUserSync();
  }
};

export default authService;